import React, { useEffect, useState, useCallback, useRef } from "react";
import styled from "@emotion/styled";
import { motion, AnimatePresence } from "framer-motion";
import { useWebSocket } from "../contexts/WebSocketContext";

const DashboardContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const MetricsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-height: 0;
  height: 100%;
  overflow: visible;
`;

const MetricCard = styled(motion.div)`
  padding: 8px 10px;
  background: #ffffff;
  border-radius: 16px;
  text-align: center;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  transition: all 0.2s ease;
  position: relative;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border-color: rgba(0, 0, 0, 0.1);
  }

  h3 {
    margin: 0 0 10px 0;
    font-size: 14px;
    font-weight: 500;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-family: var(--font-family);
  }

  .metric-value {
    margin: 0;
    font-size: 32px;
    font-weight: 600;
    color: #111827;
    font-family: var(--font-family);
    line-height: 1.2;
  }

  &[data-metric="agentCount"] .metric-value {
    color: ${(props) => (props.value > 0 ? "#3D3D3D" : "#111827")};
  }

  &[data-metric="emailsSent"] .metric-value {
    color: ${(props) => (props.value > 0 ? "#3D4A5C" : "#111827")};
  }

  &[data-metric="emailsOpened"] .metric-value {
    color: ${(props) => (props.value > 0 ? "#4A5B6C" : "#111827")};
  }

  &[data-metric="responses"] .metric-value {
    color: ${(props) => (props.value > 0 ? "#5C5D5A" : "#111827")};
  }

  &[data-metric="meetings"] .metric-value {
    color: ${(props) => (props.value > 0 ? "#6C5E4A" : "#111827")};
  }

  &[data-metric="responseRate"] .metric-value {
    color: ${(props) => (props.value > 0 ? "#8B6F3D" : "#111827")};
  }

  &[data-metric="conversionRate"] .metric-value {
    color: ${(props) => (props.value > 0 ? "#8B6914" : "#111827")};
  }
`;

const ErrorMessage = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #fee2e2;
  color: #991b1b;
  padding: 16px 24px;
  border-radius: 8px;
  text-align: center;
  max-width: 80%;
  z-index: 100;

  button {
    margin-top: 12px;
    padding: 8px 16px;
    background: #dc2626;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
      background: #b91c1c;
    }
  }
`;

const LoadingOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  z-index: 50;

  .loading-text {
    margin-top: 12px;
    color: #4b5563;
    font-size: 14px;
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e5e7eb;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export function PerformanceDashboard({ metrics = {}, agentCount = 0 }) {
  console.log('[PerformanceDashboard] Component rendered with metrics:', metrics);
  const { isConnected, on, off, emit, error: socketError } = useWebSocket();
  const [loading, setLoading] = useState(false); // Changed to false - don't show loading by default
  const [error, setError] = useState(null);
  const sessionStartRef = useRef(window.sessionStartTime ?? Date.now());
  const processedEmailEventKeysRef = useRef([]);
  const processedEmailEventsRef = useRef(new Set());

  const handleMetricClick = (metricType) => {
    const eventMap = {
      emailsSent: 'emails_sent',
      emailsOpened: 'opened',
      responses: 'responses',
      meetings: 'meetings'
    };
    if (eventMap[metricType]) {
      window.dispatchEvent(new CustomEvent('openActivityCanvas', {
        detail: { mode: 'list', filter: eventMap[metricType] }
      }));
    }
  };

  const [localMetrics, setLocalMetrics] = useState({
    agentCount: agentCount || 0,
    emailsSent: metrics.emailsSent || 0,
    emailsOpened: metrics.emailsOpened || 0,
    responses: metrics.responses || 0,
    meetings: metrics.meetings || 0,
    responseRate: metrics.responseRate || 0,
    conversionRate: metrics.conversionRate || 0,
  });

  // Track processed response events to prevent double counting
  const processedResponsesRef = useRef(new Set());

  const retryConnection = useCallback(() => {
    setError(null);
    setLoading(true);
    emit("get_metrics", {});
  }, [emit]);

  useEffect(() => {
    if (socketError) {
      setError(socketError.message || "Failed to connect to server");
      setLoading(false);
    }
  }, [socketError]);

  useEffect(() => {
    let timeoutId;

    if (isConnected) {
      setError(null);

      // Use emit for socket events
      emit("get_metrics", {});

      // Set a timeout to show error if metrics don't load
      // Increased timeout from 10000 to 30000 (30 seconds) to allow more time for metrics to load
      timeoutId = setTimeout(() => {
        if (loading) {
          // Instead of showing an error, just silently stop the loading state
          // This prevents showing error messages when the backend is working but just slow
          setLoading(false);
        }
      }, 30000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isConnected, emit, loading]);

  useEffect(() => {
    // Update localMetrics immediately when agentCount changes
    setLocalMetrics((prev) => ({
      ...prev,
      agentCount: agentCount,
    }));
    console.log("PerformanceDashboard: Updating agent count to:", agentCount);
  }, [agentCount]);

  useEffect(() => {
    // Update localMetrics when metrics prop changes
    console.log("PerformanceDashboard: Metrics prop changed:", metrics);
    setLocalMetrics((prev) => ({
      ...prev,
      emailsSent: metrics.emailsSent || prev.emailsSent || 0,
      emailsOpened: metrics.emailsOpened || prev.emailsOpened || 0,
      responses: metrics.responses || prev.responses || 0,
      meetings: metrics.meetings || prev.meetings || 0,
      responseRate: metrics.responseRate || prev.responseRate || 0,
      conversionRate: metrics.conversionRate || prev.conversionRate || 0,
    }));
    // Stop loading once we have metrics
    if (metrics.emailsSent !== undefined) {
      setLoading(false);
    }
  }, [metrics]);

  useEffect(() => {
    if (!isConnected) return;

    const handleMetricsUpdate = (message) => {
      console.log('[PerformanceDashboard] Received metrics_update:', message);

      // Handle both direct metrics and wrapped format
      const metrics = message?.content?.metrics || message?.metrics || message;

      if (!metrics || typeof metrics !== 'object') {
        console.warn('[PerformanceDashboard] Invalid metrics format:', message);
        return;
      }

      setLocalMetrics((prevMetrics) => {
        // Debug logging to track unexpected changes
        if (metrics.emailsOpened !== undefined && metrics.emailsOpened !== prevMetrics.emailsOpened) {
          console.log(`[PerformanceDashboard] emailsOpened changing: ${prevMetrics.emailsOpened} → ${metrics.emailsOpened}`);
        }

        return {
          ...prevMetrics,
          // Only update metrics that are explicitly provided and valid
          emailsSent: typeof metrics.emailsSent === 'number' ? metrics.emailsSent : prevMetrics.emailsSent,
          emailsOpened: typeof metrics.emailsOpened === 'number' ? metrics.emailsOpened : prevMetrics.emailsOpened,
          responses: typeof metrics.responses === 'number' ? metrics.responses : prevMetrics.responses,
          meetings: typeof metrics.meetings === 'number' ? metrics.meetings : prevMetrics.meetings,
          responseRate: typeof metrics.responseRate === 'number' ? metrics.responseRate : prevMetrics.responseRate,
          conversionRate: typeof metrics.conversionRate === 'number' ? metrics.conversionRate : prevMetrics.conversionRate,
          // Keep agentCount from previous state
          agentCount: prevMetrics.agentCount,
        };
      });
      setLoading(false);
    };

    const handleEmailSent = (event = {}) => {
      const rawTimestamp =
        event?.timestamp ??
        event?.sortTime ??
        event?.email_data?.timestamp;
      let eventTime = null;

      if (typeof rawTimestamp === 'number') {
        eventTime = rawTimestamp;
      } else if (typeof rawTimestamp === 'string') {
        const parsed = Date.parse(rawTimestamp);
        if (!Number.isNaN(parsed)) {
          eventTime = parsed;
        }
      }

      if (eventTime === null || Number.isNaN(eventTime)) {
        eventTime = Date.now();
      }

      const sessionStart = sessionStartRef.current || Date.now();
      if (eventTime < sessionStart) {
        console.log('[PerformanceDashboard] Ignoring stale email_sent event', {
          eventTime,
          sessionStart,
        });
        return;
      }

      const messageId =
        event?.email_data?.message_id ||
        event?.email_data?.id ||
        event?.email_data?.thread_id;
      const prospectEmail =
        event?.prospect_email ||
        event?.email_data?.recipient_email ||
        'unknown';
      const dedupeKey = messageId
        ? `id:${messageId}`
        : `ts:${eventTime}-email:${prospectEmail}`;

      if (processedEmailEventsRef.current.has(dedupeKey)) {
        console.log('[PerformanceDashboard] Duplicate email_sent event ignored', {
          dedupeKey,
        });
        return;
      }

      processedEmailEventsRef.current.add(dedupeKey);
      processedEmailEventKeysRef.current.push(dedupeKey);
      if (processedEmailEventKeysRef.current.length > 200) {
        const oldestKey = processedEmailEventKeysRef.current.shift();
        if (oldestKey) {
          processedEmailEventsRef.current.delete(oldestKey);
        }
      }

      setLocalMetrics((prevMetrics) => ({
        ...prevMetrics,
        emailsSent: (prevMetrics.emailsSent || 0) + 1,
      }));
    };

    const handleEmailOpened = (event) => {
      console.log('[PerformanceDashboard] handleEmailOpened called with event:', event);
      setLocalMetrics((prevMetrics) => {
        const newCount = (prevMetrics.emailsOpened || 0) + 1;
        console.log(`[PerformanceDashboard] Incrementing emailsOpened: ${prevMetrics.emailsOpened} → ${newCount}`);
        return {
          ...prevMetrics,
          emailsOpened: newCount,
        };
      });
    };

    const handleEmailResponded = (event) => {
      console.log('[PerformanceDashboard] handleEmailResponded called with event:', event);

      // Create unique key for this response (include timestamp for extra uniqueness)
      const responseKey = `${event.thread_id}-${event.recipient_email}-${event.timestamp}`;

      if (processedResponsesRef.current.has(responseKey)) {
        console.log('[PerformanceDashboard] Response already processed, ignoring duplicate');
        return;
      }

      // Mark as processed
      processedResponsesRef.current.add(responseKey);

      // Immediate UI feedback
      setLocalMetrics((prevMetrics) => {
        const newCount = (prevMetrics.responses || 0) + 1;
        console.log(`[PerformanceDashboard] Incrementing responses: ${prevMetrics.responses} → ${newCount}`);
        return {
          ...prevMetrics,
          responses: newCount,
        };
      });
    };

    const handleMetricsUpdateNeeded = () => {
      console.log('[PerformanceDashboard] handleMetricsUpdateNeeded - fresh metrics will be sent automatically');
      // No action needed - the backend will automatically emit metrics_update
    };

    const handleMeetingBooked = (message) => {
      console.log('[PerformanceDashboard] Meeting booked:', message);

      // Increment meetings count immediately for responsive UI
      setLocalMetrics(prev => ({
        ...prev,
        meetings: prev.meetings + 1
      }));

      // Show notification
      console.log(`🎉 Meeting booked with ${message.recipient_email}! ${message.meeting_title || 'Meeting'} scheduled for ${message.meeting_date || 'TBD'}`);
    };

    // Register event handlers for metrics updates and email events
    on("metrics_update", handleMetricsUpdate);
    on("metrics_update_needed", handleMetricsUpdateNeeded);
    on("email_sent", handleEmailSent);
    on("email_opened", handleEmailOpened);
    on("email_responded", handleEmailResponded);
    on("meeting_booked", handleMeetingBooked);

    return () => {
      off("metrics_update", handleMetricsUpdate);
      off("metrics_update_needed", handleMetricsUpdateNeeded);
      off("email_sent", handleEmailSent);
      off("email_opened", handleEmailOpened);
      off("email_responded", handleEmailResponded);
      off("meeting_booked", handleMeetingBooked);
    };
  }, [isConnected, on, off, sessionStartRef, processedEmailEventKeysRef, processedEmailEventsRef, processedResponsesRef]);

  const cardVariants = {
    initial: { scale: 0.96, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    exit: { scale: 0.96, opacity: 0 },
  };

  if (error) {
    return (
      <DashboardContainer>
        <ErrorMessage
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
        >
          <p>{error}</p>
          <button onClick={retryConnection}>Try Again</button>
        </ErrorMessage>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <MetricsGrid>
        <AnimatePresence>
          {loading && (
            <LoadingOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="loading-spinner" />
              <p className="loading-text">Loading metrics...</p>
            </LoadingOverlay>
          )}
        </AnimatePresence>

        <MetricCard
          variants={cardVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          data-metric="agentCount"
          value={localMetrics.agentCount}
        >
          <h3>Agents</h3>
          <p className="metric-value">{localMetrics.agentCount}</p>
        </MetricCard>

        <MetricCard
          variants={cardVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          data-metric="emailsSent"
          value={localMetrics.emailsSent}
          clickable={true}
          onClick={() => handleMetricClick('emailsSent')}
        >
          <h3>Emails Sent</h3>
          <p className="metric-value">{localMetrics.emailsSent}</p>
        </MetricCard>

        <MetricCard
          variants={cardVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          data-metric="emailsOpened"
          value={localMetrics.emailsOpened}
          clickable={true}
          onClick={() => handleMetricClick('emailsOpened')}
        >
          <h3>Emails Opened</h3>
          <p className="metric-value">{localMetrics.emailsOpened}</p>
        </MetricCard>

        <MetricCard
          variants={cardVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          data-metric="responses"
          value={localMetrics.responses}
          clickable={true}
          onClick={() => handleMetricClick('responses')}
        >
          <h3>Responses</h3>
          <p className="metric-value">{localMetrics.responses}</p>
        </MetricCard>

        <MetricCard
          variants={cardVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          data-metric="meetings"
          value={localMetrics.meetings}
          clickable={true}
          onClick={() => handleMetricClick('meetings')}
        >
          <h3>Meetings</h3>
          <p className="metric-value">{localMetrics.meetings}</p>
        </MetricCard>

        <MetricCard
          variants={cardVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          data-metric="responseRate"
          value={localMetrics.responseRate}
        >
          <h3>Response Rate</h3>
          <p className="metric-value">{localMetrics.responseRate}</p>
        </MetricCard>

        <MetricCard
          variants={cardVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          data-metric="conversionRate"
          value={localMetrics.conversionRate}
        >
          <h3>Conversion Rate</h3>
          <p className="metric-value">{localMetrics.conversionRate}</p>
        </MetricCard>
      </MetricsGrid>
    </DashboardContainer>
  );
}

export default React.memo(PerformanceDashboard);
